import { currentUserQuery } from "@common/api"
import { cannedWorkPackageQuery } from "@common/api/quoting/canned"
import { taskGroupsQuery } from "@common/api/quoting/task-group"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { TaskListPage } from "@/routes/_layout/templates/-components/task-list-page"
import { prefetchTasks } from "@/routes/_layout/templates/-components/task-table"
import {
  taskFilterSchema,
  taskGroupings,
} from "@/routes/_layout/templates/-lib/filters"

export const Route = createFileRoute(
  "/_layout/templates/quotes_/$packageId/_list",
)({
  validateSearch: (search) => taskFilterSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, params: { packageId }, deps }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    const readable = hasPermission(me, "quote_task_group:read")
    await Promise.all([
      readable &&
        queryClient.ensureQueryData(taskGroupsQuery({ deleted: false })),
      prefetchTasks(
        queryClient,
        deps,
        packageId,
        taskGroupings(packageId, readable),
      ),
    ])
  },
  component: TemplateQuoteTasksPage,
})

function TemplateQuoteTasksPage(): React.ReactElement {
  const filters = Route.useSearch()
  const { packageId } = Route.useParams()
  const { data: item } = useSuspenseQuery(
    cannedWorkPackageQuery({ package_id: packageId }),
  )
  const navigate = useNavigate()
  const canCreate = useHasPermission("canned_work_package:create")

  return (
    <TaskListPage
      name="Template quote"
      filters={filters}
      packageId={packageId}
      onOverview={() =>
        navigate({
          to: "/templates/quotes/$packageId/overview",
          params: { packageId },
        })
      }
      actions={
        canCreate &&
        !item.archived_at && (
          <Button asChild size="sm">
            <Link
              to="/templates/quotes/$packageId/tasks/create"
              params={{ packageId }}
              search={true}
            >
              New template task
            </Link>
          </Button>
        )
      }
    />
  )
}
